import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

export interface Vendor {
  id?: number;
  vendorType: string;

  // Company Information
  companyName: string;
  businessAddress: {
    buildingNameNumber: string;
    street: string;
    districtAreaBarangay: string;
    cityTown: string;
    stateProvinceRegion: string;
    postalZipCode: string;
    country: string;
  };
  dateEstablished: string;
  companyType: string;
  companyRegistrationNumber: string;
  taxIdentificationNumber: string;
  regulatoryCompliance: string;

  // Top Officer
  topOfficer: {
    name: string;
    designation: string;
    landlineNumber: string;
    mobileNumber: string;
    emailAddress: string;
  };

  // Contact Person
  contactPerson: {
    name: string;
    designation: string;
    landlineNumber: string;
    mobileNumber: string;
    emailAddress: string;
  };

  // Accounting Contact
  accountingContact: {
    name: string;
    designation: string;
    landlineNumber: string;
    mobileNumber: string;
    emailAddress: string;
  };

  // Banking Information
  banking: {
    bankName: string;
    accountName: string;
    accountNumber: string;
    bankAddress: string;
    swiftCode: string;
  };

  // Business Presence
  headquarters: string;
  globalPresence: string;
  website: string;
  networkAgencyMembership: string;
  referredByCompanyName: string;
  dateStartedEngagement: string;

  // Services Offered (for Agents)
  servicesOffered: {
    airFreightForwarding: boolean;
    oceanFreightForwarding: boolean;
    customsClearanceBrokerage: boolean;
    warehousingDistribution: boolean;
    supplyChainLogistics: boolean;
    projectCargoHandling: boolean;
    dangerousGoodsHandling: boolean;
    freightInsurance: boolean;
    doorToDoorDelivery: boolean;
    importExportDocumentation: boolean;
    ecommerceCustomsClearance: boolean;
  };

  notes: string;
}

@Component({
  selector: 'app-vendor-details-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './vendor-details-modal.html',
  styleUrl: './vendor-details-modal.css'
})
export class VendorDetailsModalComponent implements OnInit {
  showModal = false;
  vendors: Vendor[] = [];
  selectedVendor: Vendor | null = null;
  isEditing = false;
  searchTerm = '';

  vendorTypes = [
    'Overseas agents',
    'Co-loaders (LCL consolidators)',
    'Carrier/Shipping Line',
    'Airlines / Air cargo GSAs',
    'Port / terminal operators',
    'BOC CFS operators',
    'Customs brokers (outsourced)',
    'Trucker',
    'Origin trucking / hauliers',
    'Warehouses',
    'Cargo insurance providers',
    'Container yards (CY) / empty depots',
    'Surveyors / cargo inspectors',
    'Permit facilitators (FDA, DA, BOI, PEZA, etc.)',
    'Packaging / palletizing providers'
  ];

  companyTypes = [
    'Corporation',
    'Partnership',
    'Sole Proprietor'
  ];

  newVendor: Vendor = this.getEmptyVendor();

  constructor() {}

  ngOnInit(): void {
    // Load vendors from service later
  }

  get currentVendor(): Vendor {
    return this.selectedVendor || this.newVendor;
  }

  open(): void {
    this.showModal = true;
    this.loadVendors();
  }

  close(): void {
    this.showModal = false;
    this.selectedVendor = null;
    this.isEditing = false;
  }

  loadVendors(): void {
    // TODO: Load from backend service
    console.log('Loading vendors...');
  }

  openAddVendorForm(): void {
    this.newVendor = this.getEmptyVendor();
    this.isEditing = true;
    this.selectedVendor = null;
  }

  editVendor(vendor: Vendor): void {
    this.selectedVendor = { ...vendor };
    this.isEditing = true;
  }

  saveVendor(): void {
    if (this.selectedVendor?.id) {
      // Update existing vendor
      console.log('Updating vendor:', this.selectedVendor);
      // TODO: Call backend service
    } else {
      // Create new vendor
      console.log('Creating new vendor:', this.newVendor);
      // TODO: Call backend service
    }
    this.isEditing = false;
    this.loadVendors();
  }

  deleteVendor(vendor: Vendor): void {
    if (confirm(`Are you sure you want to delete ${vendor.companyName}?`)) {
      console.log('Deleting vendor:', vendor);
      // TODO: Call backend service
      this.loadVendors();
    }
  }

  cancelEdit(): void {
    this.isEditing = false;
    this.selectedVendor = null;
    this.newVendor = this.getEmptyVendor();
  }

  get filteredVendors(): Vendor[] {
    if (!this.searchTerm) return this.vendors;

    const term = this.searchTerm.toLowerCase();
    return this.vendors.filter(v =>
      v.companyName.toLowerCase().includes(term) ||
      v.vendorType.toLowerCase().includes(term)
    );
  }

  private getEmptyVendor(): Vendor {
    return {
      vendorType: '',
      companyName: '',
      businessAddress: {
        buildingNameNumber: '',
        street: '',
        districtAreaBarangay: '',
        cityTown: '',
        stateProvinceRegion: '',
        postalZipCode: '',
        country: ''
      },
      dateEstablished: '',
      companyType: '',
      companyRegistrationNumber: '',
      taxIdentificationNumber: '',
      regulatoryCompliance: '',
      topOfficer: {
        name: '',
        designation: '',
        landlineNumber: '',
        mobileNumber: '',
        emailAddress: ''
      },
      contactPerson: {
        name: '',
        designation: '',
        landlineNumber: '',
        mobileNumber: '',
        emailAddress: ''
      },
      accountingContact: {
        name: '',
        designation: '',
        landlineNumber: '',
        mobileNumber: '',
        emailAddress: ''
      },
      banking: {
        bankName: '',
        accountName: '',
        accountNumber: '',
        bankAddress: '',
        swiftCode: ''
      },
      headquarters: '',
      globalPresence: '',
      website: '',
      networkAgencyMembership: '',
      referredByCompanyName: '',
      dateStartedEngagement: '',
      servicesOffered: {
        airFreightForwarding: false,
        oceanFreightForwarding: false,
        customsClearanceBrokerage: false,
        warehousingDistribution: false,
        supplyChainLogistics: false,
        projectCargoHandling: false,
        dangerousGoodsHandling: false,
        freightInsurance: false,
        doorToDoorDelivery: false,
        importExportDocumentation: false,
        ecommerceCustomsClearance: false
      },
      notes: ''
    };
  }
}
